import './clan-parallel-game.css';
import { Crown, Swords, Ticket, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { InfoLeagueProps } from '../../../../leagues';
import { useState } from 'react';
import { ROUTES } from '../../../../routes';
import { useIsClanMember } from '../../../../hooks';
import { NoClan } from '../no-clan';

type ClanParallelGameProps = {
  league: InfoLeagueProps;
  parallelTickets?: number;
};

export const ClanParallelGame = ({
  league,
  parallelTickets = 0,
}: ClanParallelGameProps) => {
  const { hasJoined } = useIsClanMember();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  return (
    <div
      className={`clanParallelGame-container ${
        hasJoined ? 'clanParallelGame-active' : 'clanParallelGame-disabled'
      }`}
    >
      {hasJoined && (
        <>
          <div className="clanParallelGame-blur clanParallelGame-blurTop" />
          <div className="clanParallelGame-blur clanParallelGame-blurBottom" />
        </>
      )}

      {/* HEADER */}
      <ClanParallelGameHeader parallelTickets={parallelTickets} />

      {/* CONTENT */}
      <ClanParallelGameContent
        league={league}
        isExpanded={isExpanded}
        parallelTickets={parallelTickets}
        setIsExpanded={setIsExpanded}
      />
    </div>
  );
};

type ClanParallelGameHeaderProps = {
  parallelTickets: number;
};

const ClanParallelGameHeader = ({
  parallelTickets,
}: ClanParallelGameHeaderProps) => {
  const { hasJoined } = useIsClanMember();

  return !hasJoined ? (
    <NoClan />
  ) : (
    <div className="clanParallelGameHeader-container">
      <div className="clanParallelGameHeader-row">
        {/* LEFT */}
        <div className="clanParallelGameHeader-left">
          <div className="clanParallelGameHeader-iconBox clanParallelGameHeader-iconBoxActive">
            <Swords className="clanParallelGameHeader-icon" stroke="#000011" />
          </div>

          <div className="clanParallelGameHeader-text">
            <h2 className="clanParallelGameHeader-title clanParallelGameHeader-titleActive">
              Arena del Clan
            </h2>

            <p className="clanParallelGameHeader-subtitle clanParallelGameHeader-subtitleActive">
              Competencia exclusiva para miembros de clanes
            </p>
          </div>
        </div>

        {/* RIGHT */}
        <div className="clanParallelGameHeader-right">
          <div className="clanParallelGameHeader-ticketBox">
            <div className="clanParallelGameHeader-ticketLabel">
              Tickets Disponibles
            </div>

            <div className="clanParallelGameHeader-ticketValue">
              <Ticket
                className="clanParallelGameHeader-ticketIcon"
                stroke="#CAFB10"
              />
              {parallelTickets}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

type ClanParallelGameContentProps = {
  parallelTickets: number;
  league: InfoLeagueProps;
  isExpanded: boolean;
  setIsExpanded: (value: boolean) => void;
};

const ClanParallelGameContent = ({
  parallelTickets,
  league,
  isExpanded,
  setIsExpanded,
}: ClanParallelGameContentProps) => {
  const { hasJoined } = useIsClanMember();

  return (
    <div className="clanParallelGameContent-container">
      {hasJoined ? (
        <div className="clanParallelGameContent-wrapper">
          {/* Info tickets */}
          <div className="clanParallelGameContent-info">
            <div className="clanParallelGameContent-infoRow">
              <Ticket
                className="clanParallelGameContent-ticketIcon"
                stroke="#CAFB10"
              />
              <div className="clanParallelGameContent-infoText">
                <h3 className="clanParallelGameContent-infoTitle">
                  ¿Cómo obtener tickets?
                </h3>
                <p className="clanParallelGameContent-infoDesc">
                  Por cada tarjeta que{' '}
                  <strong className="highlight">compres</strong> en esta liga,
                  recibirás automáticamente{' '}
                  <strong className="highlight">1 ticket</strong> para la Arena
                  del Clan.
                </p>
                <div className="clanParallelGameContent-note">
                  <p className="clanParallelGameContent-noteText">
                    <strong className="noteHighlight">Nota:</strong> Las
                    tarjetas de regalo NO generan tickets. Solo las compras
                    directas cuentan para la Arena del Clan.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Rewards */}
          <div className="clanParallelGameContent-rewards">
            <div className="clanParallelGameContent-infoRow">
              <Crown
                className="clanParallelGameContent-crownIcon"
                stroke="#CAFB10"
              />
              <div className="clanParallelGameContent-infoText">
                <h3 className="clanParallelGameContent-rewardsTitle">
                  Premios Exclusivos del Clan
                  <TrendingUp
                    className="clanParallelGameContent-trendingIcon"
                    stroke="#90F7FF"
                  />
                </h3>
                <p className="clanParallelGameContent-rewardsDesc">
                  Compite con otros miembros de clanes por premios adicionales
                  que se suman a los de la liga principal.
                </p>

                <ClanParallelExpandButton
                  isExpanded={isExpanded}
                  setIsExpanded={setIsExpanded}
                />

                {isExpanded && (
                  <div className="clanParallelGameContent-expanded">
                    <div className="clanParallelGameContent-row">
                      <span className="clanParallelGameContent-label">
                        🥇 1° Lugar Arena
                      </span>
                      <span className="clanParallelGameContent-value first">
                        Premio especial
                      </span>
                    </div>
                    <div className="clanParallelGameContent-row">
                      <span className="clanParallelGameContent-label">
                        🥈 2° Lugar Arena
                      </span>
                      <span className="clanParallelGameContent-value second">
                        Premio especial
                      </span>
                    </div>
                    <div className="clanParallelGameContent-row">
                      <span className="clanParallelGameContent-label">
                        🥉 3° Lugar Arena
                      </span>
                      <span className="clanParallelGameContent-value third">
                        Premio especial
                      </span>
                    </div>

                    <p className="clanParallelGameContent-footnote">
                      * Los premios de la Arena del Clan son independientes y
                      adicionales a los premios de la liga principal
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* CTA */}
          {hasJoined ? (
            <Link
              to={`${ROUTES.LEAGUE}/${league.path}${ROUTES.CLAN_ARENA}`}
              target="_top"
              className="clanParallelGameContent-cta"
            >
              <Swords
                className="clanParallelGameContent-ctaIcon"
                stroke="#000011"
              />
              Entrar a la Arena del Clan
              <span className="clanParallelGameContent-ctaBadge">
                {parallelTickets} tickets
              </span>
            </Link>
          ) : (
            <div className="clanParallelGameContent-empty">
              <p className="clanParallelGameContent-emptyText">
                Compra tarjetas para obtener tickets y acceder a la Arena del
                Clan
              </p>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default ClanParallelGameContent;

type ClanParallelExpandButtonProps = {
  isExpanded: boolean;
  setIsExpanded: (value: boolean) => void;
};

const ClanParallelExpandButton = ({
  isExpanded,
  setIsExpanded,
}: ClanParallelExpandButtonProps) => {
  return (
    <button
      onClick={() => setIsExpanded(!isExpanded)}
      className="clanParallelExpandButton-button"
    >
      {isExpanded ? 'Ocultar detalles' : 'Ver detalles de premios'}
      <span
        className="clanParallelExpandButton-arrow"
        style={{
          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
        }}
      >
        ▼
      </span>
    </button>
  );
};
